import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, ChangeDetectorRef, AfterViewChecked, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Subscription } from 'rxjs';
import { ButtonIconProps } from '@yooga-tecnologia/mantra/loader';

import { LoadingComponent } from '../../../components/loading/loading.component';
import { Payment } from '../../../domain/models/payment';
import { Table } from '../../../domain/models/table';
import { ConfigService } from '../../../services/config.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { PaymentService } from '../../../domain/services/payment.service';
import { PaymentValuesService } from '../../../domain/services/payment-values.service';
import { TableService } from '../../../domain/services/table.service';
import { UserService } from '../../../domain/services/user.service';
import { CurrencyPipe } from '../../../pipes/currency.pipe';
import { PaymentEnabledDto } from '../../../services/payments/dto/payment-enabled-dto';
import { WalletKind } from '../../../services/payments/models/tuna.model';
import { TunaWalletService } from '../../../services/payments/wallet.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { PaymentDiscount } from '../../../domain/models/payment-discount';
import { AppBarComponent } from '../../../components/app-bar/app-bar.component';
import { TableValuesSummaryListComponent } from '../../../components/table-values-summary-list/table-values-summary-list.component';
import { CashbackApiService } from '../../../services/cashback/cashback-api.service';
import { SocketService } from '../../../services/socket/socket.service';

@Component({
  selector: 'app-select-payment',
  imports: [
    CommonModule,
    AppBarComponent,
    LoadingComponent,
    TableValuesSummaryListComponent,
  ],
  providers: [CurrencyPipe],
  templateUrl: './select-payment.component.html',
  styleUrl: './select-payment.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SelectPaymentComponent implements AfterViewChecked, OnInit {

  @ViewChild('applePayFinalContainer', { static: false }) applePayFinalContainer: ElementRef;
  @ViewChild('googlePayFinalContainer', { static: false }) googlePayFinalContainer: ElementRef;

  voucherInput: HTMLInputElement;
  tunaSession = '';
  walletInitialized = false;
  walletButtonsMountAttempted = false;
  cashbackInfoLoaded = false;
  walletContainersVisible = true;
  applePayEnabled = false;
  googlePayEnabled = false;

  uuid: string;
  loading: boolean = false;
  tableSubscription: Subscription;
  table: Table;
  walletType: WalletKind = WalletKind.GOOGLE_PAY;
  customerEmail: string = '';
  customerId: string | undefined = '';
  paidWithWallet = false;
  paymentDiscountText: string = '';
  paymentDiscount?: PaymentDiscount;
  pollingMessage: string = '';
  selectedPayment?: Payment;
  pageTitle: string = '';

  mappedPayments: Payment[] = [];
  loadingPayments: boolean = true;
  applePayReady: boolean = false;
  googlePayReady: boolean = false;

  // Feature flags
  hasCashback: boolean = false;
  hasVoucher: boolean = false;

  // Voucher
  loadingVoucher: boolean = false;
  voucherInputValue: string;
  voucherInputMessage: string = '';
  voucherInputStatus: 'success' | 'error' | 'default' = 'default';
  voucherAppliedId = 0;

  // Cashback
  cashbackLabel: string = '';
  /** Saldo disponível para resgate, em reais */
  cashbackBalance: number = 0;
  /** Último cartTotal (centavos) enviado pro fiber em loadCashbackInfo —
   *  usado pra evitar refetch quando o total da mesa não mudou. */
  private lastCashbackCartTotal: number | null = null;
  /** Cashback a ganhar nesta compra, em reais */
  cashbackToEarn: number = 0;
  /** Campanha vigente gera cashback (ACTIVE). PAUSED só resgata → esconde "vai ganhar". */
  canEarn: boolean = false;
  /** Tem saldo, mas a mesa ainda não atingiu o mínimo de resgate da campanha. */
  belowMinimumRedeemValue: boolean = false;
  /** Quanto falta na mesa para liberar o resgate, em reais */
  missingValueToRedeem: number = 0;
  /** Flag para indicar se o cashback será utilizado */
  useCashback: boolean = true;

  pageTitleMap = {
    'TOTAL': 'Pagar o total da mesa',
    'PARTIAL': 'Pagar o que consumi',
    'CUSTOM': 'Pagar valor personalizado',
  };

  backButtonProps: ButtonIconProps = {
    icon: 'arrow-left',
    color: 'neutral',
    variant: 'regular',
    size: 'medium',
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tableService: TableService,
    private paymentService: PaymentService,
    private userService: UserService,
    private toastr: ToastrService,
    private walletService: TunaWalletService,
    private utilsService: UtilsService,
    private cdr: ChangeDetectorRef,
    private currencyPipe: CurrencyPipe,
    private configService: ConfigService,
    private cashbackApiService: CashbackApiService,
    public paymentValuesService: PaymentValuesService,
    private socketService: SocketService,
  ) {
    this.uuid = String(this.route.snapshot.paramMap.get('uuid'));
    this.loading = true;
    this.table = this.tableService.getTable();

    if (!this.table) {
      this.tableService.loadTable(this.uuid)
    }

    this.pageTitle = this.pageTitleMap[this.table.paymentType as keyof typeof this.pageTitleMap] || '';
  }

  ngOnInit(): void {
    if (!this.tableSubscription) {
      this.tableSubscription = this.tableService.$openedTable.subscribe((table: Table | null) => {
        if(table) {
          const wasInitialized = this.walletInitialized;
          const prevCartTotal = this.lastCashbackCartTotal;
          this.table = table;
          this.getPaymentsEnabled();
          // Re-busca cashbackToEarn quando o total a pagar muda (cupom/fidelity
          // aplicado removido recarregam a mesa via loadTable). Sem isso o
          // badge "Você vai ganhar X de cashback" mostra o valor do total
          // anterior à mutação.
          const currentCartTotal = Math.round(this.paymentValuesService.getTableFinalTotal(this.table) * 100);
          if (this.hasCashback && prevCartTotal != null && prevCartTotal !== currentCartTotal) {
            this.loadCashbackInfo();
          }
          if (wasInitialized) {
            this.remountWalletButtons();
          }
          this.getPaymentDiscountText(this.table);
        }

        if (table && table.vouchers?.length) {
          this.voucherInputValue = table.vouchers[0].voucherName;
          this.voucherAppliedId = table.vouchers[0].voucherId;
          this.voucherInputStatus = 'success';
          this.voucherInputMessage = 'Cupom aplicado com sucesso';
        }
      })
    }

    const voucherConfig = this.configService.getValue('modulo_voucher_v2');
    this.hasVoucher = voucherConfig === 'true' || voucherConfig === '1';
    this.loadCashbackInfo();

    setTimeout(() => {
      this.voucherInput = document.getElementById('voucher-input') as HTMLInputElement;

      if (this.voucherAppliedId !== 0) { // Se tiver cupom aplicado, desabilita o input
        this.voucherInput.setAttribute('disabled', 'true');
      }
    }, 1000);
  }

  /**
   * Angular lifecycle hook - called after every view check
   * Used to mount wallet buttons once ViewChild containers are ready
   */
  ngAfterViewChecked(): void {
    // Only attempt to mount buttons once, after Tuna session is initialized
    if (this.tunaSession && !this.walletButtonsMountAttempted && this.cashbackInfoLoaded && (this.applePayEnabled || this.googlePayEnabled)) {
      console.log('[SelectPayment] ngAfterViewChecked - tentando montar botões');
      console.log('[SelectPayment] applePayContainer exists:', !!this.applePayFinalContainer?.nativeElement);
      console.log('[SelectPayment] googlePayContainer exists:', !!this.googlePayFinalContainer?.nativeElement);

      // Check if at least one container is ready
      const appleReady = this.applePayEnabled && this.applePayFinalContainer && this.applePayFinalContainer.nativeElement;
      const googleReady = this.googlePayEnabled && this.googlePayFinalContainer && this.googlePayFinalContainer.nativeElement;

      if (appleReady || googleReady) {
        this.walletButtonsMountAttempted = true;
        console.log('[SelectPayment] ViewChild containers prontos, chamando prepareButtons()');
        this.prepareButtons();
      }
    }
  }

  getPaymentDiscountText(table: Table) {
    let discount = table.getPaymentDiscount();

    if (!discount || discount?.discountValue == 0) {
      this.paymentDiscount = undefined;
      this.paymentDiscountText = '';
      return '';
    }

    this.paymentDiscount = discount;
    this.paymentDiscountText = discount.discountType === 'PERCENTAGE'
      ? `Desconto de ${discount.discountValue}%`
      : `Desconto de R$ ${discount.discountValue}`;

    return this.paymentDiscountText;
  }

  getPaymentsEnabled() {
    console.log('[SelectPayment] getPaymentsEnabled chamado, idi:', this.table.idi);
    this.paymentService.getPaymentsEnabled(this.table.idi).subscribe({
      next: (payments: PaymentEnabledDto[]) => {
        console.log('[SelectPayment] Resposta payments/enabled:', JSON.stringify(payments));
        this.checkPaymentsEnabled(payments);
        this.loading = false;
      },
      error: (e) => {
        console.error('[SelectPayment] Erro ao buscar payments enabled:', e)
        this.loading = false;
      },
    })
  }

  private isIOSDevice(): boolean {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const maxTouchPoints = navigator.maxTouchPoints;

    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isMacSafari = /Macintosh/.test(userAgent) && /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    const isMacWithTouch = platform === 'MacIntel' && maxTouchPoints > 1;

    return isIOS || isMacSafari || isMacWithTouch;
  }

  async handleSelectPayment(payment: Payment) {
    this.loading = true;
    console.log('[SelectPayment] handleSelectPayment chamado - payment:', payment.name, '| nfceType:', payment.nfceType, '| paidWithWallet:', this.paidWithWallet);

    // Cashback agora é aplicado no toggle (handleCashbackChange) via /cashback/apply
    // — persistido em user_table_cashback. O redeem real no fiber roda no webhook PAID
    // do nest, então não há nada extra a fazer aqui antes do executePayment.

    this.tableService.setPayment(payment);

    const paymentToExecute = this.tableService.getPayment();
    console.log('[SelectPayment] executePayment - tableId:', this.table.id, '| totalUserPaid:', this.table.totalUserPaid, '| totalToPay:', this.table.totalToPay);

    this.paymentService.executePayment(this.table, paymentToExecute).subscribe({
      next: async (response) => {
        console.log('[SelectPayment] executePayment SUCESSO - uuid:', response.data.uuid, '| status:', response.data.status);

        if (this.paidWithWallet) {
          console.log('[SelectPayment] Wallet flow - iniciando polling para uuid:', response.data.uuid);
          const result = await this.waitForPaymentConfirmation(response.data.uuid);
          this.loading = false;
          console.log('[SelectPayment] Polling finalizado - resultado:', result);

          if (result === 'PAID') {
            this.socketService.emit(
              'mesa:update',
              JSON.stringify({
                uuid: crypto.randomUUID(),
              }),
            );
            this.router.navigate([`${this.uuid}/payment-finished`]);
          } else if (result === 'FAILED') {
            this.resetPaymentAttempt();
            this.toastr.error('Pagamento recusado', 'Erro no pagamento');
          } else {
            this.resetPaymentAttempt();
            this.toastr.warning('Tempo esgotado aguardando confirmação', 'Timeout');
          }
        } else {
          // PIX vai para tela de QR code
          this.loading = false;
          console.log('[SelectPayment] PIX flow - redirecionando para tela de QR code');
          this.router.navigate([`${this.uuid}/payment`]);
        }
      },
      error: (e) => {
        this.loading = false;
        console.error('[SelectPayment] executePayment ERRO - status:', e?.status, '| error:', JSON.stringify(e?.error), '| message:', e?.message);

        this.resetPaymentAttempt();

        // Mensagem do back é técnica e em inglês ("Error while create intent");
        // o detalhe real fica no console.error acima.
        this.toastr.error(
          'Tente novamente ou escolha outra forma de pagamento.',
          'Não foi possível concluir o pagamento',
        );
      }
    });
  }

  /**
   * Libera a tela para nova tentativa após falha. O cardId da wallet é de uso
   * único, então o botão do rodapé não pode reenviar o token já queimado.
   */
  private resetPaymentAttempt(): void {
    this.paidWithWallet = false;
    this.selectedPayment = undefined;
  }

  handleBackButtonClick() {
    this.router.navigate(['..'], {relativeTo: this.route});
  }

  handleVoucherApply() {
    this.loadingVoucher = true;

    const user = this.userService.getUser();

    if (!user?.clientId) {
      this.toastr.error('Por favor, tente novamente','Ocorreu um erro ao aplicar o cupom')
      return;
    }

    this.tableService.setVoucher(user?.clientId, this.voucherInputValue).subscribe({
      next: (res: any) => {
        this.voucherAppliedId = res.voucherId;
        this.voucherInputStatus = 'success';
        this.voucherInputMessage = 'Cupom aplicado com sucesso';
        this.voucherInput.setAttribute('disabled', 'true');
      },
      error: (e) => {
        this.voucherInputStatus = 'error';
        this.voucherInputMessage = e?.error?.message || e?.error?.error || e?.message || 'Erro ao aplicar o cupom';
        this.voucherInput.removeAttribute('disabled');
      },
    }).add(() => {
      this.loadingVoucher = false;
    });
  }

  handleVoucherRemove() {
    this.loadingVoucher = true;
    const user = this.userService.getUser();
    this.tableService.removeVoucher(this.voucherAppliedId, user?.clientId).subscribe({
      next: () => {
        this.voucherAppliedId = 0;
        this.voucherInputValue = '';
        this.voucherInputStatus = 'default';
        this.voucherInputMessage = '';
        this.toastr.success('Cupom removido com sucesso');
        this.voucherInput.removeAttribute('disabled');
      },
      error: () => {
        this.toastr.error('Por favor, tente novamente','Ocorreu um erro ao remover o cupom')
        this.voucherInput.removeAttribute('disabled');
      },
    }).add(() => {
      this.loadingVoucher = false;
    });
  }

  handleVoucherInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.voucherInputValue = input.value;
  }

  async handleCashbackChange() {
    const isCurrentlyApplied = (this.table.cashbackApplied ?? 0) > 0;
    const nextUseCashback = !isCurrentlyApplied;
    const user = this.userService.getUser();
    try {
      if (nextUseCashback) {
        if (!user?.clientId) return;
        const cashbackToApply = Math.min(
          this.cashbackBalance,
          this.table.tableFinalValue,
        );
        if (cashbackToApply <= 0) return;
        // apply dispara o redeem real no fiber (debita o saldo do cliente e
        // cria o PIN). Se a Tuna recusar o pagamento depois, o usuário precisa
        // desmarcar o checkbox pra estornar — não há rollback automático.
        await firstValueFrom(
          this.cashbackApiService.applyCashback({
            idi: this.table.idi,
            cartId: this.table.tableCartId,
            cashbackValue: cashbackToApply,
            clientId: user.clientId,
            cartTotal: Math.round(
              this.paymentValuesService.getTableFinalTotal(this.table) * 100,
            ),
          }),
        );
      } else {
        // remove faz o /redeem/cancel no fiber (devolve saldo + PIN) antes do
        // soft-delete do user_table_cashback.
        await firstValueFrom(
          this.cashbackApiService.removeAppliedCashback(
            this.table.idi,
            this.table.tableCartId,
          ),
        );
      }
      this.useCashback = nextUseCashback;
      // recarregar mesa → cashbackApplied/remaining vêm do back (fonte da verdade).
      // outros clientes da mesa verão o desconto no próximo loadTable deles.
      // O subscribe de $openedTable já chama remountWalletButtons quando a mesa atualiza.
      this.tableService.loadTable(this.uuid, true);
    } catch (e: any) {
      console.warn('[Cashback] handleCashbackChange erro:', e?.message);
      this.toastr.error(
        e?.error?.message || 'Falha ao alterar cashback',
        'Erro',
      );
    }
  }

  private remountWalletButtons(): void {
    console.log('[Wallet] remount: novo getTotal()=', this.getTotal());
    this.walletInitialized = false;
    this.applePayReady = false;
    this.googlePayReady = false;
    this.walletButtonsMountAttempted = false;
    this.walletContainersVisible = false;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.walletContainersVisible = true;
      this.cdr.detectChanges();
    }, 0);
  }

  private loadCashbackInfo(): void {
    const user = this.userService.getUser();
    if (!user?.clientId) {
      this.cashbackInfoLoaded = true;
      return;
    }

    // Cashback (ganho/resgate) é exclusivo do cliente dono da mesa — fixado no
    // 1º pedido do GD. Outros usuários na mesma mesa (ex: amigo pedindo junto)
    // não recebem nem resgatam, então a UI de cashback fica oculta pra eles.
    if (user.clientId !== this.table.officialClientId) {
      console.log('[Cashback] usuário não é o dono da mesa (clientId:', user.clientId, '| officialClientId:', this.table.officialClientId, ') - cashback oculto');
      this.cashbackInfoLoaded = true;
      return;
    }

    // Cashback é gerado sobre o total da mesa (com voucher/fidelity/desconto/
    // taxa-de-serviço/acréscimo aplicados), NÃO sobre table.total cru nem
    // sobre o que o usuário vai pagar agora (em PARTIAL/CUSTOM é parcial).
    const cartTotal = Math.round(this.paymentValuesService.getTableFinalTotal(this.table) * 100);
    this.lastCashbackCartTotal = cartTotal;

    console.log('[Cashback] getCashbackInfo - idi:', this.table.idi, '| clientId:', user.clientId, '| cartTotal:', cartTotal);
    this.cashbackApiService.getCashbackInfo(this.table.idi, user.clientId, cartTotal).subscribe({
      next: async (info) => {
        if (info.enabled) {
          this.hasCashback = true;
          this.canEarn = info.canEarn === true;
          this.cashbackBalance = (info.balance ?? 0) / 100;
          this.cashbackToEarn = (info.cashbackToEarn ?? 0) / 100;

          // Mesa abaixo do mínimo de resgate: o saldo existe mas não pode ser usado.
          const minimumRedeem = (info.minimumRedeemOrderValue ?? 0) / 100;
          const tableTotal = cartTotal / 100;
          this.belowMinimumRedeemValue =
            this.cashbackBalance > 0 && minimumRedeem > 0 && tableTotal < minimumRedeem;
          this.missingValueToRedeem = this.belowMinimumRedeemValue ? minimumRedeem - tableTotal : 0;

          // PAUSED (canEarn=false) só resgata: nunca monta label de "vai ganhar".
          const cashbackApplied = Number(this.table.cashbackApplied) || 0;
          if (cashbackApplied > 0) {
            this.cashbackLabel = 'Cashback de ' + this.formatCurrency(cashbackApplied) + ' aplicado';
          } else if (this.belowMinimumRedeemValue) {
            this.cashbackLabel = 'Adicione mais ' + this.formatCurrency(this.missingValueToRedeem) + ' no pedido para resgatar o cashback';
          } else if (this.cashbackBalance > 0) {
            this.cashbackLabel = 'Resgatar ' + this.formatCurrency(this.cashbackBalance) + ' de desconto';
          } else if (this.canEarn && this.cashbackToEarn > 0) {
            this.cashbackLabel = 'Você vai ganhar ' + this.formatCurrency(this.cashbackToEarn) + ' de cashback nesta compra';
          } else if (this.canEarn) {
            this.cashbackLabel = 'Você vai ganhar cashback nesta compra';
          } else {
            this.cashbackLabel = '';
          }

          // Hidrata o toggle pelo que já está persistido no back (user_table_cashback).
          // Sem auto-apply: como o apply dispara fiber redeem (debita saldo +
          // cria PIN), o usuário precisa marcar explicitamente.
          this.useCashback = (this.table.cashbackApplied ?? 0) > 0;
        }
        this.cashbackInfoLoaded = true;
        this.cdr.detectChanges();
      },
      error: (e) => {
        console.warn('[Cashback] Erro ao buscar informações de cashback - status:', e?.status, '| message:', e?.message, '| error:', JSON.stringify(e?.error));
        this.cashbackInfoLoaded = true;
        this.cdr.detectChanges();
      },
    });
  }

  handleChangePaymentMethod(method: Payment) {
    this.selectedPayment = method;
    this.applyPaymentMethodDiscount(method);
  }

  handleContinueButtonClick() {
    if(!this.selectedPayment) {
      return;
    }
    this.handleSelectPayment(this.selectedPayment);
  }

  private formatPaymentName(methodName: string): string {
    const nameMap: Record<string, string> = {
      'PIX': 'PIX',
      'APPLE_PAY': 'Apple Pay',
      'GOOGLE_PAY': 'Google Pay',
    };

    return nameMap[methodName] || methodName;
  }

  private formatCurrency(value: number): string {
    return this.currencyPipe.transform(value) || '';
  }

  /**
   * Aplica o desconto por forma de pagamento quando PIX é selecionado
   */
  private applyPaymentMethodDiscount(method: Payment): void {
    // Limpa desconto anterior se não for PIX ou não tiver desconto
    if (method.nfceType !== 17 || !method.discount || method.discount <= 0) {
      this.tableService.setPaymentMethodDiscount(null);
      return;
    }

    // Calcula o valor do desconto baseado no tipo
    const discountType = this.paymentDiscount?.discountType || 'PERCENTAGE';
    const discountValue = method.discount;

    this.tableService.setPaymentMethodDiscount({
      type: discountType,
      value: discountValue,
    });
  }

  /**
   * Wait for payment confirmation via polling
   * @param paymentUuid The UUID of the payment to check
   * @returns 'PAID' if confirmed, 'FAILED' if cancelled/refunded, 'TIMEOUT' if max attempts reached
   */
  private async waitForPaymentConfirmation(paymentUuid: string): Promise<'PAID' | 'FAILED' | 'TIMEOUT'> {
    const maxAttempts = 30;
    const intervalMs = 1000;

    for (let i = 0; i < maxAttempts; i++) {
      this.pollingMessage = `Confirmando pagamento... (${i + 1}/${maxAttempts})`;

      try {
        const details = await firstValueFrom(this.paymentService.getDetails(paymentUuid));

        if (details.status === 'PAID') {
          this.pollingMessage = '';
          return 'PAID';
        }
        if (['CANCELLED', 'REFUNDED'].includes(details.status)) {
          this.pollingMessage = '';
          return 'FAILED';
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
      }

      await new Promise(r => setTimeout(r, intervalMs));
    }

    this.pollingMessage = '';
    return 'TIMEOUT';
  }

  /**
   * Initialize and mount wallet buttons for final payment
   */
  private async initializeWalletForFinalPayment(): Promise<void> {
    console.log('[SelectPayment] initializeWalletForFinalPayment - walletInitialized:', this.walletInitialized);
    if (this.walletInitialized) {
      console.log('[SelectPayment] Wallet já inicializada, pulando');
      return;
    }

    this.customerId = this.userService.getUser() ? this.userService.getUser()?.id.toString() : this.utilsService.generateUUID();
    this.customerEmail = `customer_${this.customerId}@yooga.com.br`;
    console.log('[SelectPayment] customerId:', this.customerId, '| table.total:', this.table?.total);

    if (!this.customerId || !this.customerEmail || !this.table || !this.table.total) {
      console.warn('[SelectPayment] Dados insuficientes para inicializar wallet - customerId:', !!this.customerId, '| customerEmail:', !!this.customerEmail, '| table:', !!this.table, '| table.total:', this.table?.total);
      return;
    }

    try {
      // Get or create Tuna session
      if (!this.tunaSession) {
        console.log('[SelectPayment] Criando sessão Tuna...');
        const wire = await firstValueFrom(this.paymentService.createTunaSession(this.customerId, this.customerEmail, this.table.idi));
        console.log('[SelectPayment] Resposta createTunaSession:', JSON.stringify(wire));

        this.tunaSession = this.paymentService.normalizeSessionToken(wire);
        console.log('[SelectPayment] tunaSession normalizado:', this.tunaSession ? this.tunaSession.substring(0, 20) + '...' : 'VAZIO');

        this.walletService.initTuna(this.tunaSession);
        console.log('[SelectPayment] Tuna SDK inicializada');

        // Trigger change detection to ensure ViewChild containers are ready
        this.cdr.detectChanges();
        console.log('[SelectPayment] Change detection triggered, prepareButtons será chamado no ngAfterViewChecked');
      }
    } catch (error) {
      console.error('[SelectPayment] Erro ao inicializar wallet:', error);
    }
  }

  private async prepareButtons(): Promise<void> {
    const totalParaWallet = this.getTotal();
    console.log('[Wallet][prepareButtons] paymentType:', this.table.paymentType, '| useCashback:', this.useCashback, '| cashbackBalance:', this.cashbackBalance, '| table.remaining:', this.table.remaining, '| getTotal():', totalParaWallet);

    // Mount the appropriate wallet button
    if (this.applePayEnabled && this.applePayFinalContainer && this.applePayFinalContainer.nativeElement) {
      try {
        const success = await this.walletService.mountApplePay(
          '#applePayFinalContainer',
          this.getTotal(),
          (cardId: string) => this.finalizePayment(WalletKind.APPLE_PAY, cardId)
        );
        if (success) {
          this.walletInitialized = true;
          this.applePayReady = true; // Marca como pronto após montar
        } else {
          this.applePayEnabled = false; // Desabilita se não conseguir montar
        }
      } catch (error) {
        this.applePayEnabled = false;
      }
    } else if (this.applePayEnabled) {
      console.warn('[SelectPayment] Apple Pay habilitado mas container NÃO encontrado no DOM');
    }

    if (this.googlePayEnabled && this.googlePayFinalContainer && this.googlePayFinalContainer.nativeElement) {
      try {
        console.log('[SelectPayment] Montando Google Pay...');
        const success = await this.walletService.mountGooglePay(
          '#googlePayFinalContainer',
          this.getTotal(),
          (cardId: string) => this.finalizePayment(WalletKind.GOOGLE_PAY, cardId)
        );
        console.log('[SelectPayment] mountGooglePay resultado:', success);

        if (success) {
          this.walletInitialized = true;
          this.googlePayReady = true;
          this.cdr.detectChanges();
        } else {
          throw new Error('Google Pay não está disponível');
        }
      } catch(error) {
        console.error('[SelectPayment] Erro ao montar Google Pay:', error);
      }
    } else if (this.googlePayEnabled) {
      console.warn('[SelectPayment] Google Pay habilitado mas container NÃO encontrado no DOM');
    }
  }

  private finalizePayment(walletKind: WalletKind, cardId: string) {
    console.log('[SelectPayment] finalizePayment - walletKind:', walletKind, '| cardId:', cardId ? cardId.substring(0, 10) + '...' : 'VAZIO');
    this.loading = true;
    const payment: Payment = {
      name: walletKind,
      displayName: this.formatPaymentName(walletKind),
      icon: '',
      nfceType: walletKind === WalletKind.GOOGLE_PAY ? 19 : 18,
      customerId: this.customerId,
      tokenSession: this.tunaSession,
      sessionId: this.utilsService.generateUUID(),
      correlationId: '001',
      customerEmail: this.customerEmail,
      cardId: cardId,
    };
    console.log('[SelectPayment] Payment montado - tokenSession:', this.tunaSession ? this.tunaSession.substring(0, 20) + '...' : 'VAZIO', '| sessionId:', payment.sessionId);

    this.selectedPayment = payment;
    this.tableService.setPaymentMethodDiscount(null);
    this.paidWithWallet = true;

    this.handleSelectPayment(payment);
  }

  private mapPaymentsFromAPI(
    paymentsEnabled: PaymentEnabledDto[],
    walletEnabled = true,
    googlePayAllowed = true,
  ): Payment[] {
    const paymentMethodsMap: Record<string, { icon: string; nfceType: number }> = {
      'PIX': { icon: 'pix', nfceType: 17 },
      'APPLE_PAY': { icon: 'apple', nfceType: 18 },
      'GOOGLE_PAY': { icon: 'google', nfceType: 19 },
    };

    const walletMethods = ['APPLE_PAY', 'GOOGLE_PAY'];

    return paymentsEnabled
      .filter(provider => provider.enabled)
      .flatMap(provider =>
        provider.methods
          .filter(method => method.enabled && paymentMethodsMap[method.name])
          .filter(method => walletEnabled || !walletMethods.includes(method.name))
          .filter(method => googlePayAllowed || method.name !== 'GOOGLE_PAY')
          .map(method => {
            const config = paymentMethodsMap[method.name];
            const payment: Payment = {
              name: method.name,
              displayName: this.formatPaymentName(method.name),
              icon: config.icon,
              nfceType: config.nfceType,
            };

            if (config.nfceType === 17) {
              // Para PIX, desconto vem exclusivamente da configuração da mesa.
              // feeValue da API não é aplicado para evitar desconto indevido.
              if (
                this.paymentDiscount
                && this.paymentDiscount.discountValue > 0
                && this.table.paymentType === 'TOTAL'
              ) {
                payment.discount = this.paymentDiscount.discountValue;
                payment.discountText = this.paymentDiscount.discountType === 'PERCENTAGE'
                  ? `${this.paymentDiscount.discountValue}% de desconto`
                  : `R$ ${this.paymentDiscount.discountValue} de desconto`;
              }
            } else if (method.feeValue > 0 && method.feeType === 'PERCENTAGE') {
              // Para outros métodos (Apple Pay, Google Pay), usa o feeValue da API
              payment.discount = method.feeValue;
              payment.discountText = `${method.feeValue}% de desconto`;
            }

            return payment;
          })
      );
  }

  checkPaymentsEnabled(payments: PaymentEnabledDto[]) {
    this.loadingPayments = true; // Inicia loading

    // Wallet (Apple/Google Pay) só aparece se a config do restaurante permitir.
    // O endpoint enabled é a nível de marketplace e não conhece essa config.
    const walletEnabled = this.configService.isEnabled('digital_waiter_wallet_payment');

    // Em aparelho Apple a wallet oferecida é o Apple Pay.
    const googlePayAllowed = walletEnabled && !this.isIOSDevice();

    // Mapeia os payments da API para o formato do componente
    this.mappedPayments = this.mapPaymentsFromAPI(payments, walletEnabled, googlePayAllowed);

    // Lógica existente de Apple Pay / Google Pay
    this.applePayEnabled = walletEnabled && payments.some(p =>
      p.methods.some(m => m.name === 'APPLE_PAY' && m.enabled)
    );

    this.googlePayEnabled = googlePayAllowed && payments.some(p =>
      p.methods.some(m => m.name === 'GOOGLE_PAY' && m.enabled)
    );

    // Inicializa wallets se necessário
    if (this.applePayEnabled || this.googlePayEnabled) {
      this.initializeWalletForFinalPayment().finally(() => {
        // Remove loading após inicialização dos wallets
        this.loadingPayments = false;
      });
    } else {
      // Remove loading imediatamente se não há wallets
      this.loadingPayments = false;
    }
  }

  getSubtotal(): number {
    return this.paymentValuesService.getSubtotal(this.table);
  }

  getTotal(): number {
    return this.paymentValuesService.getTotalToPay(this.table);
  }

  getRemainingValue(): number {
    return this.paymentValuesService.getRemainingAfterPayment(this.table);
  }
}
